import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  connectCornerstone,
  disconnectCornerstone,
  getCornerstoneStatus,
  syncCornerstone,
} from "@/api/cornerstone";
import { CornerstoneCredentialsForm, type CornerstoneCredentialValues } from "@/components/cornerstone/CornerstoneCredentialsForm";
import { CornerstoneConnectionDiagnostics } from "@/components/cornerstone/CornerstoneConnectionDiagnostics";
import { QueryError } from "@/components/ui/QueryError";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { useTenant } from "@/hooks/useTenant";
import { useToasts } from "@/hooks/useToast";
import type { ApiError, CornerstoneStatus } from "@/types";

function fmtTime(value: string | null, empty: string): string {
  return value ? new Date(value).toLocaleString() : empty;
}

/** /settings/integrations/cornerstone — credentials, diagnostics, and sync controls. */
export function CornerstoneSettingsPage() {
  const qc = useQueryClient();
  const push = useToasts((s) => s.push);
  const { isAdmin } = useTenant();
  const [editing, setEditing] = useState(false);

  const status = useQuery<CornerstoneStatus, ApiError>({
    queryKey: ["integrations", "cornerstone"],
    queryFn: getCornerstoneStatus,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["integrations", "cornerstone"] });
    qc.invalidateQueries({ queryKey: ["me"] });
  };

  const onError = (err: ApiError) => {
    push(err.message ?? "Request failed", "error");
    refresh();
  };

  const connect = useMutation<CornerstoneStatus, ApiError, CornerstoneCredentialValues>({
    mutationFn: (v) => connectCornerstone({ hub_url: v.hub_url, api_key: v.api_key }),
    onSuccess: () => {
      push(
        "Cornerstone connected! The first sync pulls SOAP notes and invoices from the last 30 days.",
        "success"
      );
      setEditing(false);
      refresh();
    },
    onError,
  });

  const sync = useMutation<void, ApiError>({
    mutationFn: syncCornerstone,
    onSuccess: () =>
      push("Sync queued — new visits will appear under Appointments shortly.", "success"),
    onError,
  });

  const disconnect = useMutation<void, ApiError>({
    mutationFn: disconnectCornerstone,
    onSuccess: () => {
      push("Cornerstone disconnected; stored API key was deleted. Past audits are kept.", "success");
      refresh();
    },
    onError,
  });

  const data = status.data;
  const busy = connect.isPending || sync.isPending || disconnect.isPending;
  const showForm = isAdmin && (!data?.connected || editing);

  return (
    <div>
      <div className="page-header">
        <h1>Cornerstone integration</h1>
        <p className="page-sub">
          SOAP notes and invoices are automatically pulled from Cornerstone Hub and audited in
          real-time — no manual uploads required.
        </p>

        {/* Sync-method callout */}
        <div
          className="callout"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 16,
            borderRadius: "var(--r-md)",
          }}
        >
          <span style={{ fontSize: 20 }}>🔄</span>
          <span>
            <strong>Auto-pull active</strong> — VetGuard polls Cornerstone Hub every{" "}
            {data?.poll_minutes ?? 5} minutes for new or changed visits.
          </span>
        </div>
      </div>

      {status.isLoading && (
        <div className="card loading">
          <div className="spinner" />
          <div>Loading…</div>
        </div>
      )}
      {status.error && (
        <QueryError
          error={status.error}
          what="the Cornerstone connection"
          onRetry={() => status.refetch()}
        />
      )}

      {data && (
        <div className="settings-grid">
          <div className="stack">
            {/* ── Connected state ── */}
            {data.connected && (
              <section className="card" aria-labelledby="cs-conn-title">
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <h2 id="cs-conn-title" style={{ margin: 0 }}>
                    Connection
                  </h2>
                  <StatusBadge tone={data.status === "active" ? "synced" : "unbilled"}>
                    {data.status === "active" ? "Connected" : "Needs attention"}
                  </StatusBadge>
                </div>

                {data.last_error && (
                  <div className="callout error" role="alert" style={{ marginTop: 12 }}>
                    {data.last_error}
                  </div>
                )}

                <dl className="kv">
                  <dt>Hub URL</dt>
                  <dd className="mono">{data.hub_url}</dd>
                  <dt>API key</dt>
                  <dd className="mono">••••{data.api_key_hint}</dd>
                  <dt>Last sync</dt>
                  <dd>{fmtTime(data.last_synced_at, "Never")}</dd>
                  <dt>Poll interval</dt>
                  <dd>Every {data.poll_minutes} minutes</dd>
                </dl>

                {isAdmin && (
                  <>
                    <div className="row" style={{ flexWrap: "wrap" }}>
                      <button
                        id="cs-sync-btn"
                        className="btn btn-sm"
                        disabled={busy || data.status !== "active"}
                        onClick={() => sync.mutate()}
                      >
                        Sync now
                      </button>
                      <button
                        className="btn btn-sm"
                        disabled={busy}
                        onClick={() => setEditing((e) => !e)}
                        aria-expanded={editing}
                      >
                        {editing ? "Cancel" : "Update credentials"}
                      </button>
                      <button
                        id="cs-disconnect-btn"
                        className="btn btn-sm btn-danger"
                        disabled={busy}
                        onClick={() => {
                          if (
                            window.confirm(
                              "Disconnect Cornerstone? The stored API key will be deleted; past audits are kept."
                            )
                          ) {
                            disconnect.mutate();
                          }
                        }}
                      >
                        Disconnect
                      </button>
                    </div>
                    <CornerstoneConnectionDiagnostics disabled={busy} />
                  </>
                )}
              </section>
            )}

            {/* ── Not connected, non-admin ── */}
            {data && !data.connected && !isAdmin && (
              <div className="card muted">
                Cornerstone isn&apos;t connected. Ask a clinic admin to connect it here.
              </div>
            )}

            {/* ── Credentials form ── */}
            {showForm && (
              <CornerstoneCredentialsForm
                busy={connect.isPending}
                reconnecting={data.connected}
                onSubmit={(v) => connect.mutate(v)}
                onCancel={data.connected ? () => setEditing(false) : undefined}
              />
            )}
          </div>

          {/* ── Right sidebar: how auto-pull works ── */}
          <aside className="stack">
            <section className="card" aria-labelledby="cs-how-title">
              <h3 id="cs-how-title" style={{ margin: "0 0 10px" }}>
                How auto-pull works
              </h3>
              <ol
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  lineHeight: 1.8,
                  color: "var(--text-secondary)",
                  fontSize: 13,
                }}
              >
                <li>
                  VetGuard polls <strong>Cornerstone Hub</strong> every{" "}
                  {data.poll_minutes ?? 5} minutes.
                </li>
                <li>
                  New or updated visits are fetched — including their{" "}
                  <strong>SOAP notes</strong> and <strong>invoice lines</strong>.
                </li>
                <li>The NLP pipeline extracts clinical events from the SOAP text.</li>
                <li>Events are reconciled against the invoice to find missed charges.</li>
                <li>
                  Flags appear under <strong>Appointments</strong> in real-time.
                </li>
              </ol>

              <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border-subtle)" }}>
                <h4 style={{ margin: "0 0 6px", fontSize: 13 }}>Required API scopes</h4>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    lineHeight: 1.8,
                    color: "var(--text-secondary)",
                    fontSize: 13,
                  }}
                >
                  <li>
                    <code>visits:read</code>
                  </li>
                  <li>
                    <code>soapnotes:read</code>
                  </li>
                  <li>
                    <code>invoices:read</code>
                  </li>
                  <li>
                    <code>patients:read</code>
                  </li>
                </ul>
              </div>
            </section>

            <section className="card" aria-labelledby="cs-faq-title">
              <h3 id="cs-faq-title" style={{ margin: "0 0 10px" }}>
                Frequently asked questions
              </h3>
              <details style={{ marginBottom: 10 }}>
                <summary style={{ cursor: "pointer", fontWeight: 500 }}>
                  Does VetGuard write anything back to Cornerstone?
                </summary>
                <p className="muted" style={{ margin: "6px 0 0" }}>
                  No — VetGuard is read-only for Cornerstone. It only pulls visits, SOAP notes,
                  and invoices; it never creates or modifies records in Cornerstone.
                </p>
              </details>
              <details style={{ marginBottom: 10 }}>
                <summary style={{ cursor: "pointer", fontWeight: 500 }}>
                  How far back does the first sync go?
                </summary>
                <p className="muted" style={{ margin: "6px 0 0" }}>
                  The initial sync covers the last 30 days. Subsequent polls pick up anything
                  that changed since the previous sync.
                </p>
              </details>
              <details>
                <summary style={{ cursor: "pointer", fontWeight: 500 }}>
                  What if my clinic uses on-premises Cornerstone?
                </summary>
                <p className="muted" style={{ margin: "6px 0 0" }}>
                  VetGuard integrates with the <strong>Cornerstone Hub</strong> cloud API
                  (IDEXX cloud-enabled practices). If your clinic is purely on-premises,
                  contact VetGuard support for direct-database connectivity options.
                </p>
              </details>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}
